/**
 * Example auth api connector that lets your user log in using a defined payload
 * and returns a promise
 */

import api from '@/config/api'
import { EDITOR_SCHEMA_VERSION } from '@/constants'
import vuex from '@/store'
import catchErrors from './catchErrors'
import Axios from 'axios'
import { uploadParts } from '@/utils/multipartUpload'
import nrcs from '@/api/nrcs'
import groups from '@/api/groups'
import dayjs from 'dayjs'
import _ from 'lodash'

export default {
  ...groups,
  getServerInfo() {
    return api.get('/v2/newsdesk/server-info')
  },
  validateToken() {
    return api.get('/v2/auth/validate')
  },
  getStationAccessList() {
    return catchErrors.get(`/v2/newsdesk/access`)
  },
  getNewsDeskUsers(station = null, level = null) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/users/${_station}`, {
      params: level ? { level } : {},
      cache: {
        maxAge: 600 * 1000
      }
    })
  },

  getUserProfile(userId) {
    return catchErrors.get(`/v2/newsdesk/users/${userId}`, {
      params: {
        includeAccess: true
      },
      cache: {
        maxAge: 120 * 1000
      }
    })
  },

  fetchUsers({ filter, sort, limit = 100, page } = {}, skipCache = false) {
    let params = {}

    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key] != null && filter[key] !== '') {
          params[`filter[${key}]`] = filter[key]
        }
      })
    }

    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.per_page = limit

    return catchErrors.get(`/v2/newsdesk/users`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache
      }
    })
  },

  getExtraContacts(config = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/users/extra/${station}`, {
      cache: {
        maxAge: 600 * 1000
      },
      ...config
    })
  },

  addExtraContact({ name, email }) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/users/extra/${station}`, { name, email })
  },

  saveExtraContact({ id, name, email, newStation }) {
    const station = vuex.getters.currentStation.station
    const data = { name, email }
    if (newStation && newStation !== station) data.station = newStation
    return api.patch(`/v2/newsdesk/users/extra/${station}/${id}`, data)
  },

  deleteExtraContact(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/users/extra/${station}/${id}`)
  },

  getStations(extraParams = {}) {
    let params = {
      limit: 100,
      ...extraParams
    }
    return catchErrors.get(`/v2/newsdesk/stations`, { params })
  },
  getStationSettings(workspace) {
    const station = workspace || vuex.getters.currentStation.station
    if (!station) return
    return catchErrors.get(`/v2/newsdesk/stations/${station}`)
  },
  getStationConfigurations() {
    return catchErrors.get(`/v2/newsdesk/stations`)
  },
  addStationSettings(data) {
    return catchErrors.post('/v2/newsdesk/stations', data)
  },
  saveStationSettings(data) {
    // Don't include task and service
    const { task, service, ...settings } = data
    return catchErrors.patch(`/v2/newsdesk/stations`, settings)
  },
  getStationSecurity(station) {
    return catchErrors.get(`/v2/newsdesk/security/${station}/assoc`)
  },
  saveStationSecurity(data, station) {
    return catchErrors.patch(`/v2/newsdesk/security/${station}`, data)
  },
  // Graphic Templates (F-09)
  getGraphicTemplates(params) {
    return catchErrors.get('/v2/newsdesk/graphic-templates', { params })
  },
  createGraphicTemplate(data) {
    return catchErrors.post('/v2/newsdesk/graphic-templates', data)
  },
  updateGraphicTemplate(id, data) {
    return catchErrors.patch(`/v2/newsdesk/graphic-templates/${id}`, data)
  },
  deleteGraphicTemplate(id) {
    return catchErrors.delete(`/v2/newsdesk/graphic-templates/${id}`)
  },

  // Connectors (F-30 CONN)
  getConnectors(params) {
    const filterParams = {}
    if (params?.is_active !== undefined) filterParams['filter[is_active]'] = params.is_active
    if (params?.connection_type !== undefined) filterParams['filter[connection_type]'] = params.connection_type
    return catchErrors.get('/v2/newsdesk/connectors', { params: filterParams, cache: { maxAge: 0, ignoreCache: true } })
  },
  getConnector(id) {
    return catchErrors.get(`/v2/newsdesk/connectors/${id}`, { cache: { maxAge: 0, ignoreCache: true } })
  },
  addConnector(data) {
    return catchErrors.post('/v2/newsdesk/connectors', data)
  },
  updateConnector(id, data) {
    return catchErrors.patch(`/v2/newsdesk/connectors/${id}`, data)
  },
  deleteConnector(id) {
    return catchErrors.delete(`/v2/newsdesk/connectors/${id}`)
  },
  discoverConnectorOptions(stationName, connectorId, payload) {
    return catchErrors.post(`/v2/newsdesk/stations/${stationName}/connectors/${connectorId}/discover`, payload)
  },

  getStagehandSettings(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/settings/${_station}`)
  },
  saveStagehandSettings(data) {
    return catchErrors.patch(`/v2/stagehand/settings/${data.station}`, data)
  },
  getStagehandSystems(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/${_station}/systems`, {
      cache: {
        maxAge: 1000
      },
      timeout: 0
    })
  },
  getStagehandInfo(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/${_station}/info`)
  },
  stopStagehandSystem(systemName) {
    let _station = vuex.getters.currentStation.station
    return catchErrors.patch(`/v2/stagehand/${_station}/systems/${systemName}`, { data: {} }, { params: { stop: true } })
  },
  startStagehandSystem(systemName) {
    let _station = vuex.getters.currentStation.station
    return catchErrors.patch(`/v2/stagehand/${_station}/systems/${systemName}`, { data: {} }, { params: { start: true } })
  },
  getWorkspaceStats(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/workspace/stats/${_station}`, {
      cache: {
        maxAge: 30 * 1000
      }
    })
  },
  getStagehandJobs(station) {
    let _station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/stagehand/${_station}/jobs?include=watchables`)
  },
  getPipelineDetails(id) {
    return catchErrors.get(`/v2/stagehand/pipelines/${id}`)
  },

  getMyTasks(skipCache) {
    let station = vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/tasks/${station}/active/assigned?include=story`, {
      cache: {
        maxAge: 10 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getMyAssignments(skipCache, station) {
    station = station ?? vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/assignments`, {
      params: {
        station
      },
      cache: {
        maxAge: 120 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  saveTask(story_id, data) {
    if (data.id) {
      // update task
      return catchErrors.patch(`/v2/newsdesk/tasks/${story_id}/${data.id}`, data)
    } else {
      // add task
      return catchErrors.post(`/v2/newsdesk/tasks/${story_id}`, data)
    }
  },
  deleteTask(story_id, id) {
    if (id) {
      // delete task
      return catchErrors.delete(`/v2/newsdesk/tasks/${story_id}/${id}`)
    }
  },

  // welcome links

  saveWelcomeLink(data) {
    let station = vuex.getters.currentStation.station
    if (data.id) {
      // update link
      return catchErrors.patch(`/v2/newsdesk/links/${station}/${data.id}`, data)
    } else {
      // add link
      return catchErrors.post(`/v2/newsdesk/links/${station}`, data)
    }
  },
  getWelcomeLinks() {
    let station = vuex.getters.currentStation.station
    return catchErrors.get(`/v2/newsdesk/links/${station}`)
  },
  deleteWelcomeLink(data) {
    if (data.id) {
      // delete link
      let station = vuex.getters.currentStation.station
      return catchErrors.delete(`/v2/newsdesk/links/${station}/${data.id}`)
    }
  },

  // splits

  getSplits() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/splits/${station}`)
  },
  saveSplit(data) {
    const station = vuex.getters.currentStation.station
    if (data.id) {
      // update
      return api.patch(`/v2/newsdesk/splits/${station}/${data.id}`, data)
    } else {
      // add
      return api.post(`/v2/newsdesk/splits/${station}`, data)
    }
  },
  deleteSplit(data) {
    if (data.id) {
      // delete
      const station = vuex.getters.currentStation.station
      return api.delete(`/v2/newsdesk/splits/${station}/${data.id}`)
    }
  },

  clearCacheKey(key) {
    if (!key) throw new Error('No cache key provided')
    return api.delete(`/sudo/admin/show-cache/${key}`)
  },
  flushCache() {
    return api.delete(`/sudo/admin/show-cache/`)
  },
  forceQueuedJobs() {
    return api.get(`/schedule-heartbeat?access_key=89fk1ee01D54734a99789fdAec3afQb34a95`, {
      cache: {
        ignoreCache: true
      }
    })
  },
  fetchOktaGroups({ filter, sort, limit = 50, page } = {}, skipCache = false) {
    let params = {}
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key] != null && filter[key] !== '') {
          params[`filter[${key}]`] = filter[key]
        }
      })
    }
    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.per_page = limit
    return catchErrors.get(`/v2/okta/groups/paginated`, {
      params,
      cache: { maxAge: 30 * 1000, ignoreCache: skipCache }
    })
  },
  fetchOktaGroupMembers(groupId, { filter, sort, limit = 50, page } = {}, skipCache = false) {
    let params = {}
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key] != null && filter[key] !== '') {
          params[`filter[${key}]`] = filter[key]
        }
      })
    }
    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.per_page = limit
    return catchErrors.get(`/v2/okta/groups/${groupId}/members`, {
      params,
      cache: { maxAge: 30 * 1000, ignoreCache: skipCache }
    })
  },
  getOktaGroups(station) {
    let ou = station ?? ''

    if (station == 'national') {
      ou = 'kmgh'
    } else if (station == 'ews') {
      ou = 'corporate'
    }

    if (ou.length > 1) {
      return api.get(`/v2/okta/groups/ou/${ou}`, {
        cache: {
          maxAge: 300 * 1000
        }
      })
    } else {
      return api.get(`/v2/okta/groups`, {
        cache: {
          maxAge: 600 * 1000
        }
      })
    }
  },
  syncOktaGroups() {
    return catchErrors.post(`/v2/okta/groups/sync`, {}, { cache: { ignoreCache: true } })
  },
  syncOktaGroupMembers(stationId) {
    return catchErrors.post(
      `/v2/okta/stations/${stationId}/sync-group-members`,
      {},
      {
        cache: {
          ignoreCache: true
        }
      }
    )
  },
  getColumnOptions(column, skipCache = false, station) {
    station = station ?? vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/${column}/options/${station}`, {
      cache: {
        ignoreCache: skipCache
      }
    })
  },
  getAllColumnOptions(skipCache = false, station) {
    station = station ?? vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/options/${station}`, {
      cache: {
        ignoreCache: skipCache
      }
    })
  },
  saveColumnOption(column, data, station) {
    station = station ?? vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/${column}/options/${station}`, data)
  },
  deleteColumnOption(column, id, station) {
    station = station ?? vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/${column}/options/${station}/${id}`)
  },
  syncColumnOptions(column) {
    // let station = vuex.getters.currentStation.station
    // return api.post(`/v2/newsdesk/${column}/options/${station}/sync`)
    return true
  },

  getTags(skipCache = false, type = 'ALL', station) {
    station = station ?? vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/tags/${station}/${type}`, {
      cache: {
        ignoreCache: skipCache
      }
    })
  },
  saveTag(type, data, station) {
    station = station ?? vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/tags/${type}/${station}`, data)
  },
  deleteTag(type, id, station) {
    station = station ?? vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/tags/${type}/${station}/${id}`)
  },

  addStoryPitch(data) {
    let station = vuex.getters.currentStation.station
    return catchErrors.post(`/v2/newsdesk/stories/${station}/pitch`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },

  addStoryFolo(parentId, dates, mediaAssetIds = []) {
    const payload = { dates: dates }
    // Selected assets move to the (latest-dated) folo so the 30-day expiration
    // keys off the folo's story_date instead of the original's.
    if (mediaAssetIds.length) payload.media_asset_ids = mediaAssetIds
    return catchErrors.post(`/v2/newsdesk/stories/${parentId}/folo`, payload)
  },

  mergeStory(from_story_id, to_story_id) {
    let station = vuex.getters.currentStation.station
    let data = {
      from_story_id, // Required
      to_story_id
    }

    // convert to formdatavar form_data = new FormData();
    let formData = new FormData()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    return catchErrors.post(`/v2/newsdesk/stories/${station}/merge`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  addAttachments(story_id, data) {
    let station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/attachments/${station}/story/${story_id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity
    })
  },

  getAttachments(scope, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/attachments/${station}/${scope}`, {
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getAttachmentsForStory(scope, id, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/attachments/${station}/${scope}/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  deleteAttachment(id) {
    return api.delete(`/v2/newsdesk/attachments/${id}`)
  },

  async completeMultiPartUpload(assetId, uploadId, parts, fileName) {
    let data = {
      uploadId,
      parts,
      fileName
    }
    const completionResponse = await catchErrors.patch(`/v2/newsdesk/media_assets/complete_upload/${assetId}`, data, {})
    if (completionResponse?.success === false) throw new Error(completionResponse?.msg ?? 'Failed to complete upload')
    return completionResponse
  },

  async abortMultiPartUpload(assetId, fileName) {
    let data = {
      fileName
    }
    const completionResponse = await catchErrors.patch(`/v2/newsdesk/media_assets/abort_upload/${assetId}`, data, {})
    if (completionResponse?.success === false) throw new Error(completionResponse?.msg ?? 'Failed to complete upload')
    return completionResponse
  },

  uploadMediaAssetMultiPart(file, multiPartUpload, progressCallback, cancelToken) {
    if (!multiPartUpload?.chunkSize) return

    return uploadParts({
      file,
      chunkSize: multiPartUpload.chunkSize,
      preSignedUrls: multiPartUpload.preSignedUrls,
      cancelToken,
      onProgress: progressCallback,
      put: (url, blob, config) => Axios.put(url, blob, { ...config, timeout: 0 })
    })
  },

  getCancelTokenSource() {
    const CancelToken = Axios.CancelToken
    const source = CancelToken.source()
    return source
  },

  async addMediaAsset(jsonData, progressCallback, cancelToken, mediaCreatedCallback) {
    let file = jsonData.file
    if (!(file instanceof File)) throw new Error('Data passed is not a File')
    let data = {
      ...jsonData,
      filename: file.name,
      size: file.size,
      // Browser File API surfaces the MIME type here; forward it so the API
      // persists mime_type on MediaAssetLocation (CreateMediaAssetRepository
      // reads `mime_type` first, then falls back to legacy `type`).
      type: file.type
    }

    delete data.file

    const mediaAssetResponse = await catchErrors.post(`/v2/newsdesk/media_assets`, data)
    let multiPartUpload = mediaAssetResponse?.data?.multiPartUpload ?? []
    let newAsset = mediaAssetResponse?.data?.media_asset
    try {
      mediaCreatedCallback(newAsset)
    } catch (error) {
      console.error('Failed to call mediaCreatedCallback')
    }
    try {
      const parts = await this.uploadMediaAssetMultiPart(file, multiPartUpload, progressCallback, cancelToken)
      const uploadResponse = await this.completeMultiPartUpload(newAsset.id, multiPartUpload.uploadId, parts, file.name)
      return { mediaAsset: mediaAssetResponse?.data?.media_asset, upload: uploadResponse?.data }
    } catch (error) {
      if (newAsset?.id) await this.abortMultiPartUpload(newAsset?.id, file.name)
      throw error
    }
  },

  updateMediaAsset(id, data) {
    return catchErrors.patch(`/v2/newsdesk/media_assets/${id}`, data)
  },
  cloneMediaAsset(id, data) {
    return catchErrors.post(`/v2/newsdesk/media_assets/${id}/clone`, data)
  },
  /**
   * Hybrid story + asset search for the "Add existing clip" picker (ND-1751).
   * Returns { data: [{ story, media_assets, matched_asset_ids, matched_on, score }], meta }.
   *
   * @param {{ station: string, q?: string, scope?: 'current'|'all', excludeAssetIds?: number[], excludeStoryId?: number, page?: number, perPage?: number }} args
   */
  searchMediaAssets({ station, q = '', scope = 'current', excludeAssetIds = [], excludeStoryId = null, page = 1, perPage = 20 }) {
    return catchErrors.get(`/v2/newsdesk/media_assets/search`, {
      params: {
        station_name: station ?? vuex.getters.currentStation.station,
        q: q || undefined,
        scope,
        exclude_asset_ids: excludeAssetIds.length ? excludeAssetIds : undefined,
        exclude_story_id: excludeStoryId || undefined,
        page,
        per_page: perPage
      },
      cache: { ignoreCache: true }
    })
  },
  /**
   * Move or copy media assets to another story in the same workspace.
   * Validation is all-or-nothing on the API side; a 422 names the offending asset ids.
   *
   * @param {{ mediaAssetIds: number[], destinationStoryId: number, mode: 'move'|'copy' }} args
   * @returns Promise<{data: Array<MediaAsset>}> - the moved rows, or the new copies
   */
  transferMediaAssets({ mediaAssetIds, destinationStoryId, mode }) {
    return catchErrors.post(`/v2/newsdesk/media_assets/transfer`, {
      media_asset_ids: mediaAssetIds,
      destination_story_id: destinationStoryId,
      mode
    })
  },

  transcribeMediaAsset(id) {
    return catchErrors.post(`/v2/newsdesk/media_assets/${id}/transcribe`)
  },

  // Media Assets - push existing media asset to video uploader
  pushMediaAsset({ destinations, id, targetFilename }) {
    return catchErrors.post(`/v2/newsdesk/media_assets/push`, { destinations: destinations?.join(','), id, targetFilename })
  },

  getMediaAsset(id, skipCache) {
    return catchErrors.get(`/v2/newsdesk/media_assets/${id}`, {
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getMediaAssetVariantUrl(id, variant) {
    return catchErrors.get(`/v2/newsdesk/media_assets/${id}/${variant}`, {
      maxRedirects: 0,
      validateStatus: (status) => status === 200 || status === 302,
      cache: {
        ignoreCache: true
      }
    })
  },
  /* getMediaAssets - get media assets
   *
   * @param {Object} params - Query parameters
   * @param {Array<string>} params.stations - Station codes to filter by (required, defaults to current station)
   * @param {string} [params.keywords] - Search keywords for partial match on name and other fields
   * @param {string} [params.name] - Filter by asset name
   * @param {string} [params.master_id] - Filter by master media asset ID
   * @param {number} [params.limit=400] - Max results to return
   * @param {number} [params.offset=0] - Pagination offset
   * @param {boolean} [params.hasErrors] - Filter by error status
   * @param {string} [params.include] - Related data to include (e.g. 'story')
   * @param Boolean skipCache
   * @returns Promise<{data: {count, offset, total, results: Array<MediaAsset>}}>
   * Response includes pagination: count (items in this page), offset (current position), total (total available)
   */
  getMediaAssets(params, skipCache = false) {
    if (!params) params = { include: 'story' }
    if (!params.stations) params.stations = [vuex.getters.currentStation.station]
    if (!params.limit) params.limit = 400

    return catchErrors.get(`/v2/newsdesk/media_assets`, {
      params,
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache
      }
    })
  },

  /* getMediaForStory - get media assets linked to a specific story
   *
   * @param {number|string} id - Story ID
   * @param {boolean} skipCache - Skip cache
   * @returns Promise<{data: Array<MediaAsset>}> - Plain array of media assets for the story
   */
  getMediaForStory(id, skipCache) {
    return catchErrors.get(`/v2/newsdesk/story_media_assets/master/${id}`, {
      cache: {
        maxAge: 45 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  deleteMediaAsset(id) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/media_assets/${station}/${id}`)
  },
  async downloadMediaAsset(id, variant = 'original') {
    const response = await api
      .post(
        `/v2/newsdesk/media_assets/${id}/${variant}/download`,
        {},
        {
          maxRedirects: 0,
          validateStatus: (status) => status === 200 || status === 302
        }
      )
      .then()
      .catch((err) => {
        console.log('assets download error', err)
      })

    const signedUrl = response?.data?.signedURL || null

    if (!signedUrl) {
      throw new Error('No signed URL found in redirect response')
    }

    return signedUrl
  },

  getComments(scope, id, skipCache = false) {
    return api.get(`/v2/newsdesk/comments/${scope}/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  sendComment(story_id, comment, mediaAssetId) {
    return api.post(`/v2/newsdesk/comments/${story_id ?? ''}`, {
      text: comment,
      media_asset_id: mediaAssetId
    })
  },
  updateComment(comment) {
    const station = vuex.getters.currentStation.station // Retrieve the current station
    return api.patch(`/v2/newsdesk/comments/${station}/${comment.id}`, comment)
  },
  deleteComment(id) {
    return api.delete(`/v2/newsdesk/comments/${id}`)
  },
  setCommentStickyStatus(id, sticky) {
    return api.patch(`/v2/newsdesk/comments/${id}/sticky/${sticky}`)
  },
  getContacts() {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/contacts/${station}?updated_at>=0`)
  },
  addContact(data, station) {
    return api.post(`/v2/newsdesk/contacts/${station}`, data)
  },
  updateContact(id, data) {
    return api.patch(`/v2/newsdesk/contacts/${id}`, data)
  },
  deleteContact(id) {
    return api.delete(`/v2/newsdesk/contacts/${id}`)
  },
  async shareContact(id, story_id) {
    // catchErrors raises on an `errors` payload returned alongside a 200. The share
    // endpoint also reports failure as `success: false`, which it does not cover, so
    // that case is turned into a throw here rather than in each caller. An older API
    // returns an empty body on success, so only an explicit false is a failure.
    const response = await catchErrors.post(`/v2/newsdesk/contacts/${id}/share/${story_id}`)

    if (response.data?.success === false) {
      throw new Error(response.data.err_msg || 'Sharing failed.')
    }

    return response
  },

  // waiting on API changes for issue #903
  // shareContact(id, story_id) {
  //   return api.post(`/v2/newsdesk/contacts/${id}/share/${story_id}`)
  // },

  getStandardLogs(station, level) {
    if (level) {
      return api.get(`/v2/newsdesk/logs/standard/${station}/${level}`)
    } else {
      return api.get(`/v2/newsdesk/logs/standard/${station}`)
    }
  },
  getFormats() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/format/options/${station}`)
  },

  async getStory({ id, station = vuex.getters.currentStation.station, includeDetails = false }, skipCache = false, deleted = false) {
    let params = {
      'id:': id,
      station,
      include: includeDetails ? 'tasks,assignments,comments,attachments,media_assets,share_requests' : undefined
    }
    if (deleted) params.deleted = 1

    const response = await api.get(`/v2/newsdesk/stories/${station}`, {
      params,
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
    if (!response.data || response.data.length === 0) {
      throw new Error(`Story with ID ${id} (station: ${station}) not found`)
    }
    return _.head(response.data)
  },
  async getStoryById(id, { include = 'tasks,assignments,comments,attachments,media_assets,share_requests', skipCache = false, deleted = false } = {}) {
    const response = await api.get(`/v2/newsdesk/stories/${id}`, {
      params: { include, ...(deleted ? { deleted: 1 } : {}) },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
    if (!response.data) {
      throw new Error(`Story with ID ${id} not found`)
    }
    return response.data
  },

  async getStoryWithDetails(id, skipCache = false, deleted = false, station = vuex.getters.currentStation.station) {
    let params = {
      include: 'tasks,assignments,comments,attachments,media_assets,share_requests',
      'id:': id
    }
    if (deleted) params.deleted = 1
    const response = await catchErrors.get(`/v2/newsdesk/stories/${station}`, {
      params,
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
    const story = _.head(response.data)
    if (!story) {
      throw new Error(`Story with ID ${id} not found`)
    }
    return story
  },

  async getCollection(master_id, skipCache = false, station = vuex.getters.currentStation.station) {
    let params = {
      include: 'tasks,assignments,comments,attachments,media_assets,share_requests'
    }
    const response = await api.get(`/v2/newsdesk/stories/${station}/${master_id}/collection`, {
      params,
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
    return response.data
  },
  getStories(filter, skipCache) {
    const station = vuex.getters.currentStation.station
    // preserve original functionality
    if (filter === '') {
      filter = 'scope=active'
    }
    // including assignments and comments so we can inject them into the quickfilter
    return api.get(`/v2/newsdesk/stories/${station}?include=assignments,comments&${filter}&limit=1000`, {
      cache: {
        maxAge: 5 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getMyStories(skipCache) {
    const station = vuex.getters.currentStation.station
    const today = dayjs().tz(vuex.getters.currentStation.timezone).format('YYYY-MM-DD')

    let params = {
      assigned: 'true',
      station: station,
      include: 'assignments,comments',
      'story_date>': today
    }

    return api.get(`/v2/newsdesk/stories`, {
      params,
      cache: {
        maxAge: 5 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getMyPitches(skipCache) {
    const station = vuex.getters.currentStation.station
    const today = dayjs().tz(vuex.getters.currentStation.timezone).format('YYYY-MM-DD')

    let params = {
      my_pitches: 'true',
      station: station,
      include: 'assignments,comments',
      'story_date>': today
    }

    return api.get(`/v2/newsdesk/stories`, {
      params,
      cache: {
        maxAge: 5 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getPlannerHeaderStories(skipCache) {
    const station = vuex.getters.currentStation.station
    let params = {
      promote_to_planner_header: 1,
      include: 'assignments,tasks,media_assets'
    }

    return api.get(`/v2/newsdesk/stories/${station}`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  getStoriesTags(data, skipCache) {
    const station = vuex.getters.currentStation.station
    let params = {
      'story_date>': data?.tags_start_date && data?.tags_start_date.length > 4 ? data.tags_start_date : undefined,
      'story_date<': data?.tags_end_date && data?.tags_start_date.length > 4 ? data.tags_end_date : undefined,
      limit: 5000,
      return_stories_tags_list_only: 1
    }

    return api.get(`/v2/newsdesk/stories/${station}/tags`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },

  generateFCCReport(station, storyIds) {
    const payload = {
      station,
      storyIds
    }
    return api.post(`/v2/newsdesk/generate-fcc-report`, payload, { timeout: 120 * 1000 })
  },

  getChanges() {
    const station = vuex.getters.currentStation.station
    if (!station) return
    return api.get(`/v2/newsdesk/changes/${station}`, {
      cache: {
        ignoreCache: true
      }
    })
  },
  getStoriesForSplits(skipCache) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stories/${station}?scope=today&story_approved:=Approved&include=assignments,tasks`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  updateStory(id, data) {
    let station = vuex.getters.currentStation.station
    return api.patch(`/v2/newsdesk/stories/${station}/${id}`, data)
  },
  updateStoryScript(id, scriptData) {
    let station = vuex.getters.currentStation.station
    return api.patch(`/v2/newsdesk/stories/${station}/${id}`, { script: scriptData })
  },
  deleteStory(row) {
    let station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/stories/${station}/${row}`)
  },

  copyAssigneeFromStory(assignmentId) {
    return catchErrors.post(`/v2/newsdesk/assignments/${assignmentId}/copy-assignee-from-story`, {})
  },

  addAssignees(type, data) {
    const idKey = { stories: 'story_id', assignments: 'assignment_id', tasks: 'task_id' }[type]
    if (!idKey) throw new Error(`Unknown assignee type: ${type}`)
    const resourceId = data[idKey]
    if (!resourceId) throw new Error(`addAssignees(${type}) requires ${idKey}`)
    return catchErrors.post(`/v2/newsdesk/${type}/${resourceId}/assignees`, data)
  },

  updateAssignee(type, { id, roleId, userId }, station = vuex.getters.currentStation.station) {
    let data = {
      role_id: roleId,
      user_id: userId
    }
    return catchErrors.patch(`/v2/newsdesk/${type}/${station}/assignees/${id}`, data)
  },

  deleteAssignee(type, id, station = vuex.getters.currentStation.station) {
    return catchErrors.delete(`/v2/newsdesk/${type}/${station}/assignees/${id}`)
  },

  getAssignment(id) {
    return api.get(`/v2/newsdesk/assignments/${id}`)
  },

  async addAssignment(data) {
    if (!data?.story_id) throw new Error('Cannot add an assignment without a story id')
    if (!data?.platform) throw new Error('Cannot add an assignment without a platform')
    return catchErrors.post(`/v2/newsdesk/assignments`, data)
  },

  async updateAssignment(id, data) {
    return catchErrors.patch(`/v2/newsdesk/assignments/${id}`, data)
  },

  // Unused
  // updateAssignments(rows) {
  //   return api.patch(`/v2/newsdesk/assignments/bulk`, rows)
  // },

  deleteAssignment(row) {
    return api.delete(`/v2/newsdesk/assignments/${row}`)
  },
  deleteAssignments(rows) {
    return api.put(`/v2/newsdesk/assignments/bulk`, rows)
  },
  pushAssignment(assignmentId) {
    return api.post(`/v2/newsdesk/assignments/${assignmentId}/push`)
  },
  repushAssignment(assignmentId) {
    return api.post(`/v2/newsdesk/assignments/${assignmentId}/repush`)
  },

  // Script Methods
  getScripts(storyId, skipCache = false) {
    return api.get(`/v2/newsdesk/scripts/story/${storyId}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  getScript(scriptId, skipCache = false) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },

  getScriptYjsState(scriptId) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/yjs-state`, {
      cache: { ignoreCache: true },
      headers: { 'X-Editor-Schema-Version': String(EDITOR_SCHEMA_VERSION) }
    })
  },
  saveScriptYjsState(scriptId, data) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/yjs-state`, data, {
      headers: { 'X-Editor-Schema-Version': String(EDITOR_SCHEMA_VERSION) }
    })
  },
  createScript(data) {
    return api.post(`/v2/newsdesk/scripts`, data)
  },
  updateScript(scriptId, data) {
    return api.patch(`/v2/newsdesk/scripts/${scriptId}`, data)
  },
  deleteScript(scriptId) {
    return api.delete(`/v2/newsdesk/scripts/${scriptId}`)
  },

  // Append script content to assignment rundown (one-way copy)
  appendScriptToAssignment(scriptId, assignmentId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/append-to-assignment/${assignmentId}`)
  },

  // Clone a script (creates unlinked copy)
  cloneScript(scriptId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/clone`)
  },

  // Set a script as primary for its story
  setScriptPrimary(scriptId) {
    return api.patch(`/v2/newsdesk/scripts/${scriptId}/set-primary`)
  },

  // Update script approval status
  updateScriptApproval(scriptId, approvalStatus) {
    return api.patch(`/v2/newsdesk/scripts/${scriptId}/approval`, {
      approval_status: approvalStatus
    })
  },

  // Script version history
  getScriptVersions(scriptId, limit = 50) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/versions`, { params: { limit } })
  },
  getScriptVersion(scriptId, versionId) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/versions/${versionId}`, { params: { include_state: true } })
  },
  createScriptVersion(scriptId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/versions`)
  },
  restoreScriptVersion(scriptId, versionId) {
    return api.post(`/v2/newsdesk/scripts/${scriptId}/versions/${versionId}/restore`)
  },

  // Script change history
  getScriptChanges(scriptId, limit = 100) {
    return api.get(`/v2/newsdesk/scripts/${scriptId}/changes`, { params: { limit } })
  },

  loadLinkedStories(masterId, includeDetails = false) {
    const station = vuex.getters.currentStation.station
    return api.get(
      `/v2/newsdesk/stories/${station}?master_id:=${masterId}&scope=archive&include=${
        includeDetails ? 'tasks,assignments,comments,attachments' : 'assignments'
      }`
    )
  },
  searchStories(keywords, startDate, endDate, limit = 500, skipCache) {
    const station = vuex.getters.currentStation.station
    let params = {
      // Callers pass either a full ISO string or YYYY-MM-DD; an empty string
      // means "no bound" and must not be sent as a filter.
      'story_date>': startDate || undefined,
      'story_date<': endDate || undefined,
      keywords,
      limit,
      include: 'assignments,tasks,media_assets'
    }

    return api.get(`/v2/newsdesk/stories/${station}`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  fetchStories(
    {
      filter,
      rootOnly,
      sort,
      groupId,
      startDate,
      endDate,
      keywords,
      stations,
      simpleSearch = false,
      limit = 500,
      page,
      groupBy,
      deleted = 0,
      include = 'assignments,tasks,media_assets,share_requests',
      extraCheckArchiveRundowns = 0
    },
    skipCache
  ) {
    if (!filter) filter = {}
    else filter = _.cloneDeep(filter)
    let params = {
      station: vuex.getters.currentStation.station,
      simple_search: simpleSearch ? 1 : 0,
      include
    }
    // Flatten filter keys so null values serialize as '' (empty string).
    // Axios drops null nested params; '' reaches StoryFilterBuilder which treats it as whereNull().
    Object.keys(filter).forEach((key) => {
      params[`filter[${key}]`] = filter[key] ?? ''
    })
    if (stations) {
      if (Array.isArray(stations)) params.stations = stations.join(',')
      else params.stations = stations
    }
    if (sort) params.sort = sort
    if (page) params.page = page
    if (limit) params.limit = limit
    if (keywords) params.keywords = keywords
    if (rootOnly) params.root_only = rootOnly
    if (deleted) params.deleted = 1
    if (startDate) params['story_date>'] = startDate
    if (endDate) params['story_date<'] = endDate
    if (groupId) params.group_id = groupId
    if (groupBy) params.group_by = groupBy
    if (extraCheckArchiveRundowns) {
      params.extra_check_archive_rundowns = extraCheckArchiveRundowns
    }

    return catchErrors.get(`/v2/newsdesk/stories`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: true
      }
    })
  },

  search(station, keywords) {
    return api.get(`/v2/newsdesk/search/${station}/${keywords}`)
  },
  searchTasks(
    { keywords, stations, start_date, end_date, limit = 50, status = 'active', page = 1, pagination = false, order_by_field = null, sort = null },
    skipCache
  ) {
    stations = stations ?? [vuex.getters.currentStation.station]
    let params = {
      limit,
      page,
      sort,
      include: 'story',
      filter: {
        status: status,
        stations: stations?.join(',')
      },
      with_pagination: pagination
    }

    if (keywords) params.filter.keywords = keywords

    if (start_date && start_date.length > 4) {
      params['start_date>'] = start_date
    }

    if (end_date && end_date.length > 4) {
      params['end_date<'] = end_date
    }

    if (order_by_field) {
      params['order_by'] = order_by_field
    }

    return catchErrors.get(`/v2/newsdesk/tasks`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },

  searchScripts({ stations, approval_status, script_type, search, date_from, date_to, sort, page = 1, limit = 50 } = {}, skipCache = true) {
    stations = stations ?? [vuex.getters.currentStation.station]
    const params = {
      stations: [].concat(stations).join(','),
      page,
      limit
    }

    if (approval_status?.length) params.approval_status = [].concat(approval_status).join(',')
    if (script_type?.length) params.script_type = [].concat(script_type).join(',')
    if (search) params.search = search
    if (date_from) params.date_from = date_from
    if (date_to) params.date_to = date_to
    if (sort) params.sort = sort

    return catchErrors.get(`/v2/newsdesk/scripts`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache
      }
    })
  },

  getPendingScriptsCount({ stations, script_type, date_from, date_to } = {}) {
    stations = stations ?? [vuex.getters.currentStation.station]
    const params = { stations: [].concat(stations).join(',') }
    if (script_type?.length) params.script_type = [].concat(script_type).join(',')
    if (date_from) params.date_from = date_from
    if (date_to) params.date_to = date_to

    return catchErrors.get(`/v2/newsdesk/scripts/pending-count`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },

  async saveDraft(storyId, assignmentId, payload) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/scripts/${station}/${storyId}/${assignmentId}`, payload)
  },

  async updateDraft(storyId, assignmentId, payload) {
    const station = vuex.getters.currentStation.station
    return api.patch(`/v2/newsdesk/scripts/${station}/${storyId}/${assignmentId}`, payload)
  },

  async deleteDraft(storyId, assignmentId) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/scripts/${station}/${storyId}/${assignmentId}`)
  },

  recoverStory(data, station) {
    return api.post(`/v2/newsdesk/stories/${station}/recover`, data)
  },

  shareStory(id, destinations) {
    let source = vuex.getters.currentStation.station
    let data = {
      story_id: id, // Required
      copyComments: true,
      copyAttachments: true,
      copyAssignmentsAsComments: true,
      copyTasks: true,
      copyMediaAssets: true
    }

    // convert to formdatavar form_data = new FormData();
    let formData = new FormData()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    for (const d of destinations) {
      formData.append('stations[]', d)
    }

    return api.post(`/v2/newsdesk/stories/${source}/share`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },

  getShareRequests(params, skipCache) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.get(`/v2/newsdesk/share_requests/${stationId}`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },

  async getPendingRequests(params, skipCache) {
    const stationId = vuex.getters.currentStation.id
    let resp = await catchErrors.get(`/v2/newsdesk/share_requests/${stationId}/inbound`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
    return resp?.data
  },

  getMonitoredStories(params, skipCache) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.get(`/v2/newsdesk/monitored_stories/${stationId}`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },

  addShareRequest({ storyId, mode, destinations, mediaAssets = [], station, expiresAt }) {
    if (!storyId || !Number.isInteger(Number(storyId))) {
      throw new Error(`addShareRequest called with invalid storyId: ${storyId}`)
    }

    const stationId = station ?? vuex.getters.currentStation.id

    let data = {
      story_id: storyId,
      mode,
      copyComments: true,
      copyAttachments: true,
      copyAssignmentsAsComments: true,
      copyTasks: true,
      copyMediaAssets: true
    }
    if (expiresAt) {
      data.expires_at = expiresAt
    }

    // convert to formdatavar form_data = new FormData();
    let formData = new FormData()
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })

    for (const d of destinations) {
      formData.append('stations[]', d)
    }
    for (const a of mediaAssets) {
      formData.append('mediaAssets[]', a)
    }

    return api.post(`/v2/newsdesk/share_requests/${stationId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },

  updateShareRequest(id, { response, mode, mediaAssets }) {
    const stationId = vuex.getters.currentStation.id
    return catchErrors.patch(`/v2/newsdesk/share_requests/${stationId}/${id}`, { response, mode, mediaAssets })
  },

  retractShareRequest(storyId) {
    const stationId = vuex.getters.currentStation.id
    return api.delete(`/v2/newsdesk/share_requests/retract/${stationId}/${storyId}`)
  },

  getUserSubscriptions(skipCache) {
    const station = vuex.getters.currentStation.station
    const params = {
      current_station: station,
      time: new Date().getTime()
    }
    return catchErrors.get('/v2/newsdesk/notifications/subscriptions', {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },
  checkStorySubscribedForNotification(storyId = '') {
    const station = vuex.getters.currentStation.station
    const params = {
      record_id: storyId || '',
      record_type: 'story',
      current_station: station,
      time: new Date().getTime()
    }
    return catchErrors.get(`/v2/newsdesk/notifications/check-subscription`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storySubscribeForNotification(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story'
    }
    return catchErrors.post('/v2/newsdesk/notifications/subscribe', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storyUnsubscribeFromNotification(storyId = '', subscribedNotificationType = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story',
      type: subscribedNotificationType || ''
    }
    return catchErrors.post('/v2/newsdesk/notifications/unsubscribe', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  getNotificationsSettings() {
    return catchErrors.get(`/v2/newsdesk/notifications/settings`)
  },

  updateNotificationsSettings(data) {
    return catchErrors.patch('/v2/newsdesk/notifications/settings', data)
  },
  resetNotificationsSettings(station) {
    return catchErrors.delete(`/v2/newsdesk/notifications/settings/${station}`)
  },

  saveOneSignalNotificationSubscription(data) {
    return catchErrors.post('/v2/newsdesk/notifications/onesignal-subscription', data)
  },

  storyNotifyCheck(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story',
      time: new Date().getTime()
    }
    return catchErrors.get(`/v2/newsdesk/notifications/notify/check`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storyNotifyEnable(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story'
    }
    return catchErrors.post('/v2/newsdesk/notifications/notify/enable', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  storyNotifyDisable(storyId = '') {
    const params = {
      record_id: storyId || '',
      record_type: 'story'
    }
    return catchErrors.post('/v2/newsdesk/notifications/notify/disable', params, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  getApiNotifications(params = {}) {
    return catchErrors.get(`/v2/newsdesk/notifications/in_app/records`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  readApiNotification(notificationId = 0) {
    return catchErrors.post(
      `/v2/newsdesk/notifications/in_app/records/${notificationId}/read`,
      {},
      {
        cache: {
          maxAge: 0,
          exclude: {
            query: true
          }
        }
      }
    )
  },

  removeApiNotification(notificationId = 0) {
    return catchErrors.delete(`/v2/newsdesk/notifications/in_app/records/${notificationId}`, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  removeApiNotifications() {
    return catchErrors.delete('/v2/newsdesk/notifications/in_app/records', {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  checkApiNotifications(params = {}) {
    return catchErrors.get(`/v2/newsdesk/notifications/in_app/records/check`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  getApiNotificationEnabledStations(params = {}) {
    return catchErrors.get(`/v2/newsdesk/notifications/enabled_stations`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },

  setApiNotificationEnabledStations(data = {}) {
    return catchErrors.post(`/v2/newsdesk/notifications/enabled_stations`, data, {
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getNotificationGroupsList() {
    const station = vuex.getters.currentStation.station
    const params = {
      sort: 'title',
      order: 'asc',
      status: 1
    }
    return api.get(`/v2/newsdesk/notifications/groups/${station}`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getNotificationGroups() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/notifications/groups/${station}`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addNotificationGroup(data, station = null) {
    station = station ?? vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/notifications/groups/${station}`, data)
  },
  updateNotificationGroup(id, data) {
    const station = vuex.getters.currentStation.station
    return api.patch(`/v2/newsdesk/notifications/groups/${station}/${id}`, data)
  },
  deleteNotificationGroup(id, station = null) {
    station = station ?? vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/notifications/groups/${station}/${id}`)
  },
  async deleteNotificationGroups(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/notifications/groups/${station}`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getTokenizedAccesses() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/tokenized-access/${station}`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addTokenizedAccess(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/tokenized-access/${station}`, data)
  },
  updateTokenizedAccess(id, data) {
    const station = vuex.getters.currentStation.station
    return api.patch(`/v2/newsdesk/tokenized-access/${station}/${id}`, data)
  },
  deleteTokenizedAccess(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/tokenized-access/${station}/${id}`)
  },
  async deleteTokenizedAccesses(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/tokenized-access/${station}`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getSelectedMediaAssetsDownloadsStats(params = {}) {
    return catchErrors.get(`/v2/newsdesk/media_assets/selected/downloads/stats`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getSelectedMediaAssetsDownloads(params = {}) {
    return catchErrors.get(`/v2/newsdesk/media_assets/selected/downloads`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  exportSelectedMediaAssetsDownloads(params = {}) {
    return catchErrors.get(`/v2/newsdesk/media_assets/selected/downloads/export`, {
      params,
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      },
      headers: {
        'Content-Type': 'text/csv'
      }
    })
  },
  setSelectedMediaAssetsDownload(data) {
    return catchErrors.post(`/v2/newsdesk/media_assets/selected/download`, data)
  },
  updateSelectedMediaAssetsDownload(id, data) {
    return catchErrors.patch(`/v2/newsdesk/media_assets/selected/download/${id}`, data)
  },
  getBucketAndKeyFromUrls(mediaAssets = []) {
    const data = {
      mediaAssets
    }
    return catchErrors.post(`/v2/newsdesk/media_assets/selected/prepare_media_assets`, data)
  },
  prepareSelectedMediaAssetsDownloadCloudfrontLink(link) {
    const data = {
      link,
      mode: 'cloudfront_link'
    }
    return catchErrors.post(`/v2/newsdesk/media_assets/selected/prepare_media_assets`, data)
  },
  getAnnouncements() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements`, {
      params: {
        is_simple_list: true,
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getAnnouncement(id) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements/${id}`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAnnouncement(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return catchErrors.post(`/v2/newsdesk/announcements`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  updateAnnouncement(id, data) {
    data.station_name = data.station_name || vuex.getters.currentStation.station_name
    return api.patch(`/v2/newsdesk/announcements/${id}`, data)
  },
  deleteAnnouncement(id) {
    return api.delete(`/v2/newsdesk/announcements/${id}`)
  },
  async deleteAnnouncements(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/announcements`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  addAnnouncementAttachments(announcementId, data) {
    return api.post(`/v2/newsdesk/announcements/${announcementId}/attachments`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  getAnnouncementAttachments(announcementId, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements/${announcementId}/attachments`, {
      params: {
        station
      },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  deleteAnnouncementAttachment(announcementId, id) {
    return api.delete(`/v2/newsdesk/announcements/${announcementId}/attachments/${id}`)
  },
  getAnnouncementsListData(skipCache) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements-list`, {
      params: {
        is_simple_list: true,
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getAnnouncementsArchiveListData(skipCache) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/announcements-list`, {
      params: {
        is_simple_list: true,
        station,
        mode: 'archive'
      },
      cache: {
        maxAge: 0,
        ignoreCache: skipCache ?? false
      }
    })
  },
  addAnnouncementReadMark(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/announcements-read-mark`, data)
  },
  recordAnnouncementView(announcementId) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/announcements-view`, { announcement_id: announcementId, station })
  },
  getAnnouncementSubscriptions() {
    return api.get(`/v2/newsdesk/announcement-subscriptions`, { params: { station: '' } })
  },
  updateAnnouncementSubscriptions(subscriptions) {
    return api.put(`/v2/newsdesk/announcement-subscriptions`, { station: '', subscriptions })
  },
  getStoryActivities(station, id, skipCache = false) {
    return api.get(`/v2/newsdesk/activity-log/story/${station}/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  getMediaAssetActivities(station, id, skipCache = false) {
    return api.get(`/v2/newsdesk/activity-log/media-asset/${station}/${id}`, {
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache
      }
    })
  },
  getInventoryCounts() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-counts`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getInventoryLocations() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-locations`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryLocation(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-locations`, data)
  },
  updateInventoryLocation(id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-locations/${id}`, data)
  },
  deleteInventoryLocation(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-locations/${id}`)
  },
  async deleteInventoryLocations(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-locations`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryTypes() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-types`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryType(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-types`, data)
  },
  updateInventoryType(id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-types/${id}`, data)
  },
  deleteInventoryType(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-types/${id}`)
  },
  async deleteInventoryTypes(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-types`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryTags() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-tags`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryTag(data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-tags`, data)
  },
  updateInventoryTag(id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-tags/${id}`, data)
  },
  deleteInventoryTag(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-tags/${id}`)
  },
  async deleteInventoryTags(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-tags`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryAssets() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-assets`, {
      params: {
        station
      },
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addInventoryAsset(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/inventory-assets`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  updateInventoryAsset(id, data) {
    const station = vuex.getters.currentStation.station
    data.append('_method', 'PATCH')
    return api.post(`/v2/newsdesk/inventory-assets/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8'
      }
    })
  },
  deleteInventoryAsset(id) {
    const station = vuex.getters.currentStation.station
    return api.delete(`/v2/newsdesk/inventory-assets/${id}`)
  },
  async deleteInventoryAssets(ids = []) {
    const station = vuex.getters.currentStation.station
    return catchErrors.delete(`/v2/newsdesk/inventory-assets`, {
      params: {
        station,
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getInventoryAssetCheckouts(inventoryAssetId, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts`, {
      params: {
        station
      },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  addInventoryAssetCheckout(inventoryAssetId, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts`, data)
  },
  updateInventoryAssetCheckout(inventoryAssetId, id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts/${id}`, data)
  },
  deleteInventoryAssetCheckout(inventoryAssetId, id) {
    return api.delete(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/checkouts/${id}`)
  },
  getInventoryAssetReservations(inventoryAssetId, skipCache) {
    let station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations`, {
      params: {
        station
      },
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  addInventoryAssetReservation(inventoryAssetId, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.post(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations`, data)
  },
  updateInventoryAssetReservation(inventoryAssetId, id, data) {
    const station = vuex.getters.currentStation.station
    data.station = station
    return api.patch(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations/${id}`, data)
  },
  deleteInventoryAssetReservation(inventoryAssetId, id) {
    return api.delete(`/v2/newsdesk/inventory-assets/${inventoryAssetId}/reservations/${id}`)
  },
  getAssigneesInventoryAssetCheckoutsList(user_ids, skipCache) {
    const station = vuex.getters.currentStation.station
    let params = { station }

    // Build filter object for Spatie QueryBuilder
    if (user_ids && user_ids.length > 0) {
      params.filter = {
        user_id: user_ids
      }
    }

    return api.get(`/v2/newsdesk/inventory-assets/assignees-checkouts`, {
      params,
      cache: {
        maxAge: 15 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getMyInventoryCheckoutsList(skipCache) {
    const station = vuex.getters.currentStation.station

    let params = {
      station: station,
      assigned: 'true'
    }

    return api.get(`/v2/newsdesk/inventory-assets/assignees-checkouts`, {
      params,
      cache: {
        maxAge: 5 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getAiProviders(params = {}) {
    return api.get(`/v2/newsdesk/ai-providers`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAiProvider(data) {
    return api.post(`/v2/newsdesk/ai-providers`, data)
  },
  updateAiProvider(id, data) {
    return api.patch(`/v2/newsdesk/ai-providers/${id}`, data)
  },
  deleteAiProvider(id) {
    return api.delete(`/v2/newsdesk/ai-providers/${id}`)
  },
  async deleteAiProviders(ids = []) {
    return catchErrors.delete(`/v2/newsdesk/ai-providers`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getAiProviderOptions() {
    return api.get(`/v2/newsdesk/ai-providers/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getAiProviderAgents(aiProviderId, params = {}) {
    return api.get(`/v2/newsdesk/ai-providers/${aiProviderId}/agents`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAiProviderAgent(aiProviderId, data) {
    return api.post(`/v2/newsdesk/ai-providers/${aiProviderId}/agents`, data)
  },
  discoverAiProviderAgents(aiProviderId) {
    return api.get(`/v2/newsdesk/ai-providers/${aiProviderId}/agents/discover`)
  },
  updateAiProviderAgent(aiProviderId, id, data) {
    return api.patch(`/v2/newsdesk/ai-providers/${aiProviderId}/agents/${id}`, data)
  },
  deleteAiProviderAgent(aiProviderId, id) {
    return api.delete(`/v2/newsdesk/ai-providers/${aiProviderId}/agents/${id}`)
  },
  async deleteAiProviderAgents(aiProviderId, ids = []) {
    return catchErrors.delete(`/v2/newsdesk/ai-providers/${aiProviderId}/agents`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getAiProviderAgentOptions() {
    return api.get(`/v2/newsdesk/ai-providers/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getAiSlotConfigs(params = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/ai-slot-configs`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addAiSlotConfig(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/stations/${station}/ai-slot-configs`, data)
  },
  updateAiSlotConfig(id, data) {
    return api.patch(`/v2/newsdesk/ai-slot-configs/${id}`, data)
  },
  deleteAiSlotConfig(id) {
    return api.delete(`/v2/newsdesk/ai-slot-configs/${id}`)
  },
  async deleteAiSlotConfigs(ids = []) {
    return catchErrors.delete(`/v2/newsdesk/ai-slot-configs`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getAiSlotConfigOptions() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/ai-slot-configs/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getStoryDistributionsPlatforms(params = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/platforms/${station}`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getStoryDistributions(id, params = {}) {
    return api.get(`/v2/newsdesk/stories/${id}/distributions`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  getStoryDistributionsReadiness(id, params = {}) {
    return api.get(`/v2/newsdesk/stories/${id}/readiness`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addStoryDistribution(id, data) {
    return api.post(`/v2/newsdesk/stories/${id}/distributions`, data)
  },
  addStoryDistributionsBatch(id, data) {
    return api.post(`/v2/newsdesk/stories/${id}/distributions/batch`, data)
  },
  updateStoryDistribution(id, data) {
    return api.patch(`/v2/newsdesk/story-distributions/${id}`, data)
  },
  deleteStoryDistribution(id) {
    return api.delete(`/v2/newsdesk/story-distributions/${id}`)
  },
  publishStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/publish`)
  },
  unpublishStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/unpublish`)
  },
  rejectStoryDistribution(id, note) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/reject`, {
      note
    })
  },
  cancelScheduleStoryDistribution(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/cancel-schedule`)
  },
  refreshStoryDistributionBody(id) {
    return api.post(`/v2/newsdesk/story-distributions/${id}/refresh-body`)
  },
  refreshStoryDistributionsForScript(scriptId) {
    return api.post(`/v2/newsdesk/story-scripts/${scriptId}/refresh-distributions`)
  },
  republishStoryDistributionsForScript(scriptId) {
    return api.post(`/v2/newsdesk/story-scripts/${scriptId}/republish-distributions`)
  },
  // Cut lists and summaries are refreshed from websocket events; never serve them from the axios cache
  getVideoCuts(mediaAssetId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts`, { cache: { ignoreCache: true } })
  },
  syncVideoCut(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/sync`)
  },
  ingestVideoCut(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/ingest`)
  },
  generateVideoCutReview(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/generate_review_link`)
  },
  getVideoCutStatus(mediaAssetId, videoCutId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/status`)
  },
  deleteVideoCut(mediaAssetId, videoCutId) {
    return api.delete(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/delete`)
  },
  restartVideoCutProcessing(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/restart`)
  },
  cancelVideoCut(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/cancel`)
  },
  getVideoCutWorkflows() {
    return api.get('/v2/newsdesk/video_cuts/workflows')
  },
  // Story-level video cuts (ND-2310): one home story, one or more source assets
  createVideoCut(payload) {
    return api.post('/v2/newsdesk/video_cuts', payload)
  },
  getStoryVideoCuts(storyId) {
    return api.get(`/v2/newsdesk/stories/${storyId}/video_cuts`, { cache: { ignoreCache: true } })
  },
  getStoryVideoCutsSummary(storyId) {
    return api.get(`/v2/newsdesk/stories/${storyId}/video_cuts/summary`, { cache: { ignoreCache: true } })
  },
  getWorkspaceVideoCuts(params = {}) {
    return api.get('/v2/newsdesk/video_cuts', { params, cache: { ignoreCache: true } })
  },
  getWorkspaceVideoCutsSummary(station) {
    return api.get('/v2/newsdesk/video_cuts/summary', { params: { station }, cache: { ignoreCache: true } })
  },
  getVideoCutChatMessages(mediaAssetId, videoCutId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/messages`)
  },
  sendVideoCutChatMessage(mediaAssetId, videoCutId, payload) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/messages`, payload)
  },
  getVideoCutChatStatus(mediaAssetId, videoCutId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/status`)
  },
  interruptVideoCutChat(mediaAssetId, videoCutId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/chat/interrupt`)
  },
  renderVideoCut(mediaAssetId, videoCutId, payload) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders`, payload)
  },
  ingestVideoCutRender(mediaAssetId, videoCutId, renderId) {
    return api.post(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders/${renderId}/ingest`)
  },
  getVideoCutRenderPlaybackUrl(mediaAssetId, videoCutId, renderId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders/${renderId}/download`)
  },
  downloadVideoCutRenderFile(mediaAssetId, videoCutId, renderId) {
    return api.get(`/v2/newsdesk/media_assets/${mediaAssetId}/video_cuts/${videoCutId}/renders/${renderId}/file`, { responseType: 'blob' })
  },

  getSequenceFormats(params = {}) {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/sequence-formats`, {
      params,
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  },
  addSequenceFormat(data) {
    const station = vuex.getters.currentStation.station
    return api.post(`/v2/newsdesk/stations/${station}/sequence-formats`, data)
  },
  updateSequenceFormat(id, data) {
    return api.patch(`/v2/newsdesk/sequence-formats/${id}`, data)
  },
  deleteSequenceFormat(id) {
    return api.delete(`/v2/newsdesk/sequence-formats/${id}`)
  },
  async deleteSequenceFormats(ids = []) {
    return catchErrors.delete(`/v2/newsdesk/sequence-formats`, {
      params: {
        ids
      },
      cache: {
        maxAge: 0,
        exclude: {
          query: true
        }
      }
    })
  },
  getSequenceFormatOptions() {
    const station = vuex.getters.currentStation.station
    return api.get(`/v2/newsdesk/stations/${station}/sequence-formats/options`, {
      cache: {
        maxAge: 0,
        ignoreCache: true
      }
    })
  }
}
